import { useQueries } from '@tanstack/react-query'
import { useApi } from '../../api/contexto'
import type { ErrorApi } from '../../api/errores'
import { TAMANO_PAGINA, type Canal, type PaginaSesiones, type SesionResumen } from './consultas'

/** Una sesión ya sabe de qué canal viene (lo necesita el hilo y el panel). */
export type SesionConCanal = SesionResumen & { canal: Canal }

export type Tipo = 'todas' | 'leads' | 'clientes'
export type Zona = 'todas' | 'castellon' | 'valencia'

/** Metadatos de presentación por canal (chip + a qué tipo/zona pertenece). */
export const CANAL_INFO: Record<Canal, { tipo: 'leads' | 'clientes'; zona: 'castellon' | 'valencia'; chip: string }> = {
  clientes: { tipo: 'clientes', zona: 'castellon', chip: 'Cliente · Castellón' },
  leads: { tipo: 'leads', zona: 'castellon', chip: 'Lead · Castellón' },
  leads_valencia: { tipo: 'leads', zona: 'valencia', chip: 'Lead · Valencia' },
}

const TODOS: Canal[] = ['clientes', 'leads', 'leads_valencia']

/** Qué canales entran según los filtros Tipo + Zona. */
export function canalesDe(tipo: Tipo, zona: Zona): Canal[] {
  return TODOS.filter((c) => {
    const i = CANAL_INFO[c]
    if (tipo !== 'todas' && i.tipo !== tipo) return false
    if (zona !== 'todas' && i.zona !== zona) return false
    return true
  })
}

/**
 * Trae los tres canales a la vez (un useQuery por canal, estables) y combina.
 * El filtrado por Tipo/Zona se hace eligiendo qué canales se habilitan, no en
 * memoria: así un canal apagado ni siquiera se consulta. Los resultados se
 * mezclan y se ordenan por fecha del último mensaje, de lo más reciente a lo
 * más antiguo, para que la bandeja unificada se lea como una sola.
 */
export function useSesionesUnificadas(tipo: Tipo, zona: Zona) {
  const api = useApi()
  const activos = new Set(canalesDe(tipo, zona))

  const consultas = useQueries({
    queries: TODOS.map((canal) => ({
      queryKey: ['sesiones-uni', canal] as const,
      enabled: activos.has(canal),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.pedir<PaginaSesiones>(`/api/conversaciones/${canal}/sesiones`, {
          parametros: { page: 1, page_size: TAMANO_PAGINA },
          senal: signal,
        }),
    })),
  })

  const cargando = consultas.some((c, i) => activos.has(TODOS[i]) && c.isPending)
  const error = consultas.find((c, i) => activos.has(TODOS[i]) && c.isError)?.error as ErrorApi | undefined

  const items: SesionConCanal[] = []
  consultas.forEach((c, i) => {
    const canal = TODOS[i]
    if (!activos.has(canal)) return
    for (const s of c.data?.items ?? []) items.push({ ...s, canal })
  })
  items.sort((a, b) => {
    const fa = a.ultima_fecha ? Date.parse(a.ultima_fecha) : 0
    const fb = b.ultima_fecha ? Date.parse(b.ultima_fecha) : 0
    return fb - fa
  })

  return {
    items,
    total: items.length,
    cargando,
    error,
    refetch: () => consultas.forEach((c) => void c.refetch()),
  }
}
